// ---------------------------------------------------------------------------
// ErrorFallback — what a boundary shows instead of a blank screen (AL-2004)
// ---------------------------------------------------------------------------
// `page` sits inside the shell, so the sidebar and topbar stay usable; `app`
// replaces everything, because whatever failed was above the shell.
//
// Uses the v11 tokens, which are defined on :root and .dark, so it also reads
// correctly on the public pages (login, reset password) outside .v11-app.
// ---------------------------------------------------------------------------

import { useTranslation } from 'react-i18next';
import { reloadPage } from './reloadPage';

export interface ErrorFallbackProps {
  readonly variant: 'page' | 'app';
  /** A chunk failed to load (older build, or offline): retrying can't help, only a reload. */
  readonly staleBuild?: boolean;
  readonly onRetry?: () => void;
}

const BUTTON_BASE =
  'rounded-md px-3 py-1.5 text-sm font-medium focus:outline-hidden focus-visible:ring-2 focus-visible:ring-v11-accent focus-visible:ring-offset-2 focus-visible:ring-offset-v11-bg';

export function ErrorFallback({ variant, staleBuild = false, onRetry }: ErrorFallbackProps) {
  const { t } = useTranslation();

  // A failed import while offline reads exactly like a stale chunk, but no
  // deploy happened: say what is actually wrong.
  const offline = staleBuild && typeof navigator !== 'undefined' && navigator.onLine === false;
  const canRetry = !staleBuild && onRetry !== undefined;

  let title: string;
  let description: string;
  if (offline) {
    title = t('app.common.errorBoundary.offlineTitle');
    description = t('app.common.errorBoundary.offlineDescription');
  } else if (staleBuild) {
    title = t('app.common.errorBoundary.staleTitle');
    description = t('app.common.errorBoundary.staleDescription');
  } else {
    title = t(
      variant === 'page'
        ? 'app.common.errorBoundary.pageTitle'
        : 'app.common.errorBoundary.appTitle'
    );
    // Only mention retrying when there is a button for it.
    description = t(
      canRetry ? 'app.common.errorBoundary.description' : 'app.common.errorBoundary.appDescription'
    );
  }

  const wrapper =
    variant === 'page'
      ? 'flex min-h-[50vh] items-center justify-center p-6'
      : 'flex min-h-screen items-center justify-center bg-v11-bg p-6';

  return (
    <div className={wrapper}>
      <div
        role="alert"
        className="flex max-w-md flex-col items-center gap-3 rounded-xl border border-v11-border bg-v11-surface p-6 text-center"
      >
        <h1 className="text-base font-semibold text-v11-text-primary">{title}</h1>
        <p className="text-sm text-v11-text-secondary">{description}</p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {canRetry && (
            <button
              type="button"
              onClick={onRetry}
              className={`${BUTTON_BASE} border border-v11-border text-v11-text-primary hover:bg-v11-bg-hover`}
            >
              {t('app.common.errorBoundary.retry')}
            </button>
          )}
          <button
            type="button"
            onClick={reloadPage}
            className={`${BUTTON_BASE} bg-v11-accent text-v11-accent-text hover:bg-v11-accent/90`}
          >
            {t('app.common.errorBoundary.reload')}
          </button>
        </div>
      </div>
    </div>
  );
}
